import React, { useRef, useState } from 'react';
import {
  Camera,
  Plus,
  Package,
  Loader2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { InventoryItem } from './index';
import { computeFreshness } from '../../../hooks/useInventory';

/**
 * PantryTab - Pantry inventory display and entry
 * 
 * Features:
 * - Rapid inline entry with auto-category detection
 * - Photo scanning for bulk add
 * - "Your Ingredients" header with sort dropdown
 * - Category headers with colored left border (when sorted by category)
 * - Flat freshness sort for "Use First"
 */
export default function PantryTab({
  // Data
  stockedItems,
  shoppingList,
  categories,
  categoryConfig,
  unitOptions,
  // Rapid entry state
  inputName,
  inputQtyNumber,
  inputQtyUnit,
  inputCategory,
  onInputNameChange,
  onInputQtyNumberChange,
  onInputQtyUnitChange,
  onInputCategoryChange,
  onAddItem,
  onKeyDown,
  // Photo state
  imagePreview,
  isAnalyzing,
  analysisStatus,
  onImageCapture,
  onAnalyzePhoto,
  onClearImage,
  // Edit state
  editingItemId,
  editName,
  editQtyNumber,
  editQtyUnit,
  editCategory,
  editPurchaseDate,
  editOpenedDate,
  editExpiryDate,
  onEditNameChange,
  onEditQtyNumberChange,
  onEditQtyUnitChange,
  onEditCategoryChange,
  onEditPurchaseDateChange,
  onEditOpenedDateChange,
  onEditExpiryDateChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  // Item actions
  onAddToShopping,
  onRemove,
  onIncrement,
  onDecrement,
  // Sort state
  sortBy,
  onSortByChange
}) {
  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null);
  
  // Collapsed categories state
  const [collapsedCategories, setCollapsedCategories] = useState({});
  
  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [cat]: !prev[cat]
    }));
  };

  // Check if item name is on shopping list
  const isItemOnShoppingList = (itemName) => {
    const lowerName = itemName.toLowerCase();
    return shoppingList.some(s => s.name.toLowerCase() === lowerName);
  };

  const handleAddAndFocus = () => {
    onAddItem();
    nameInputRef.current?.focus();
  };

  // Sort items
  const sortedItems = [...stockedItems].sort((a, b) => {
    if (sortBy === 'freshness') {
      // Flat sort by freshness (expiring first)
      const freshA = computeFreshness(a);
      const freshB = computeFreshness(b);
      const order = { expired: 0, expiring: 1, 'use-soon': 2, fresh: 3, unknown: 4 };
      const orderA = order[freshA.status] ?? 4;
      const orderB = order[freshB.status] ?? 4;
      if (orderA !== orderB) return orderA - orderB;
      // Within same status, sort by days left
      if (freshA.daysLeft !== null && freshB.daysLeft !== null) {
        return freshA.daysLeft - freshB.daysLeft;
      }
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'category') {
      // Sort by category first, then name
      const catA = a.category || 'other';
      const catB = b.category || 'other';
      if (catA !== catB) return catA.localeCompare(catB);
      return a.name.localeCompare(b.name);
    }
    // Name sort
    return a.name.localeCompare(b.name);
  });

  // Group items by category (for category view)
  const itemsByCategory = {};
  if (sortBy === 'category') {
    sortedItems.forEach(item => {
      const cat = item.category || 'other';
      if (!itemsByCategory[cat]) itemsByCategory[cat] = [];
      itemsByCategory[cat].push(item);
    });
  }

  // Count items needing attention
  const expiringCount = stockedItems.filter(item => {
    const f = computeFreshness(item);
    return f.status === 'expiring' || f.status === 'expired' || f.status === 'use-soon';
  }).length;

  // Sort options for dropdown
  const sortOptions = [
    { value: 'freshness', label: 'Use First', badge: expiringCount > 0 ? expiringCount : null },
    { value: 'name', label: 'Name' },
    { value: 'category', label: 'Category' }
  ];

  // Render a single item
  const renderItem = (item, isLast) => (
    <InventoryItem
      key={item.id}
      item={item}
      isEditing={editingItemId === item.id}
      editName={editName}
      editQtyNumber={editQtyNumber}
      editQtyUnit={editQtyUnit}
      editCategory={editCategory}
      editPurchaseDate={editPurchaseDate}
      editOpenedDate={editOpenedDate}
      editExpiryDate={editExpiryDate}
      onEditNameChange={onEditNameChange}
      onEditQtyNumberChange={onEditQtyNumberChange}
      onEditQtyUnitChange={onEditQtyUnitChange}
      onEditCategoryChange={onEditCategoryChange}
      onEditPurchaseDateChange={onEditPurchaseDateChange}
      onEditOpenedDateChange={onEditOpenedDateChange}
      onEditExpiryDateChange={onEditExpiryDateChange}
      onStartEdit={() => onStartEdit(item)}
      onSaveEdit={() => onSaveEdit(item.id)}
      onCancelEdit={onCancelEdit}
      onAddToShopping={() => onAddToShopping(item)}
      onRemove={() => onRemove(item)}
      onIncrement={() => onIncrement(item)}
      onDecrement={() => onDecrement(item)}
      isOnShoppingList={isItemOnShoppingList(item.name)}
      categories={categories}
      categoryConfig={categoryConfig}
      unitOptions={unitOptions}
      isLastItem={isLast}
      hideCategory={sortBy === 'category'} // Hide badge when grouped by category
    />
  );

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* Rapid Entry Card */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        border: `1px solid ${ABLE_COLORS.border}`,
        padding: '16px',
        marginBottom: '16px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Hidden file input for photo */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onImageCapture}
          style={{ display: 'none' }}
        />

        {/* Photo preview */}
        {imagePreview ? (
          <div style={{ marginBottom: '12px' }}>
            <img
              src={imagePreview}
              alt="Preview"
              style={{
                width: '100%',
                maxHeight: '200px',
                objectFit: 'cover',
                borderRadius: ABLE_COLORS.radiusMedium,
                marginBottom: '12px'
              }}
            />
            {analysisStatus && (
              <div style={{
                background: ABLE_COLORS.bgSubtle,
                padding: '10px',
                borderRadius: ABLE_COLORS.radiusSmall,
                textAlign: 'center',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                {isAnalyzing && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                <span style={{ color: ABLE_COLORS.textSecondary, fontSize: '14px' }}>
                  {analysisStatus}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onAnalyzePhoto}
                disabled={isAnalyzing}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: isAnalyzing ? ABLE_COLORS.bgSubtle : ABLE_COLORS.primary,
                  color: isAnalyzing ? ABLE_COLORS.textMuted : 'white',
                  border: 'none',
                  borderRadius: ABLE_COLORS.buttonRadius,
                  cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {isAnalyzing ? 'Analyzing...' : 'Scan Items'}
              </button>
              <button
                onClick={onClearImage}
                disabled={isAnalyzing}
                style={{
                  padding: '10px 16px',
                  background: ABLE_COLORS.bgSubtle,
                  color: ABLE_COLORS.textSecondary,
                  border: 'none',
                  borderRadius: ABLE_COLORS.buttonRadius,
                  cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Row 1: Ingredient name (full width on mobile) */}
            <div className="pantry-input-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <input
                ref={nameInputRef}
                type="text"
                placeholder="Add ingredient..."
                value={inputName}
                onChange={(e) => onInputNameChange(e.target.value)}
                onKeyDown={onKeyDown}
                style={{
                  flex: 1,
                  minWidth: '150px',
                  padding: '12px',
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusMedium,
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {/* Qty and Unit inline */}
              <div className="pantry-qty-group" style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="number"
                  placeholder="Qty"
                  value={inputQtyNumber}
                  onChange={(e) => onInputQtyNumberChange(e.target.value)}
                  onKeyDown={onKeyDown}
                  min="0"
                  step="any"
                  style={{
                    width: '60px',
                    padding: '12px 8px',
                    border: `1px solid ${ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <select
                  value={inputQtyUnit}
                  onChange={(e) => onInputQtyUnitChange(e.target.value)}
                  onKeyDown={onKeyDown}
                  style={{
                    width: '80px',
                    padding: '12px 8px',
                    border: `1px solid ${ABLE_COLORS.border}`,
                    borderRadius: ABLE_COLORS.radiusMedium,
                    fontSize: '14px',
                    outline: 'none',
                    background: ABLE_COLORS.neutral,
                    boxSizing: 'border-box',
                    color: inputQtyUnit ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted
                  }}
                >
                  {unitOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: Category + Camera + Add */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={inputCategory}
                onChange={(e) => onInputCategoryChange(e.target.value)}
                onKeyDown={onKeyDown}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: `1px solid ${categoryConfig[inputCategory]?.color}40`,
                  borderRadius: ABLE_COLORS.radiusMedium,
                  fontSize: '13px',
                  fontWeight: '600',
                  outline: 'none',
                  background: `${categoryConfig[inputCategory]?.color}15`,
                  boxSizing: 'border-box',
                  color: categoryConfig[inputCategory]?.color,
                  cursor: 'pointer'
                }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {categoryConfig[cat]?.label || cat}
                  </option>
                ))}
              </select>
              <button
                onClick={() => fileInputRef.current?.click()}
                title="Scan with photo"
                tabIndex={-1}
                style={{
                  padding: '12px',
                  background: ABLE_COLORS.warm,
                  border: 'none',
                  borderRadius: ABLE_COLORS.radiusMedium,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Camera size={20} color="white" />
              </button>
              <button
                onClick={handleAddAndFocus}
                style={{
                  padding: '12px 14px',
                  background: ABLE_COLORS.primary,
                  border: 'none',
                  borderRadius: ABLE_COLORS.radiusMedium,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Plus size={20} color="white" />
              </button>
            </div>
            <style>{`
              @media (max-width: 400px) {
                .pantry-input-row { flex-direction: column; }
                .pantry-qty-group { width: 100%; justify-content: flex-start; }
              }
            `}</style>
          </>
        )}
      </div>

      {/* Empty State */}
      {stockedItems.length === 0 && !inputName.trim() && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <Package size={48} color={ABLE_COLORS.textMuted} style={{ marginBottom: '16px' }} />
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary,
            marginBottom: '8px'
          }}>
            Your pantry is empty
          </h3>
          <p style={{
            fontSize: '14px',
            color: ABLE_COLORS.textMuted
          }}>
            Start typing above to add ingredients
          </p>
        </div>
      )}

      {/* Header + Sort + Items List */}
      {stockedItems.length > 0 && (
        <>
          {/* Header with Sort Dropdown */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <h2 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: ABLE_COLORS.textPrimary,
              margin: 0
            }}>
              Your Ingredients
            </h2>
            
            {/* Sort Dropdown */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px', color: ABLE_COLORS.textMuted }}>Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => onSortByChange(e.target.value)}
                style={{
                  padding: '6px 28px 6px 10px',
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusSmall,
                  fontSize: '13px',
                  fontWeight: '500',
                  background: 'white',
                  color: ABLE_COLORS.textPrimary,
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none'
                }}
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}{opt.badge ? ` (${opt.badge})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown 
                size={14} 
                color={ABLE_COLORS.textMuted}
                style={{ 
                  position: 'absolute', 
                  right: '8px', 
                  pointerEvents: 'none' 
                }} 
              />
            </div>
          </div>

          {/* Items List */}
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadiusLarge,
            border: `1px solid ${ABLE_COLORS.border}`,
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {sortBy === 'category' ? (
              // Grouped by category with collapsible headers
              Object.entries(itemsByCategory).map(([cat, items], catIdx) => {
                const config = categoryConfig[cat] || categoryConfig.other;
                const isLastCategory = catIdx === Object.keys(itemsByCategory).length - 1;
                const isCollapsed = collapsedCategories[cat];
                
                return (
                  <div key={cat}>
                    {/* Category Header - clickable to collapse/expand */}
                    <div 
                      onClick={() => toggleCategory(cat)}
                      style={{
                        padding: '10px 16px',
                        background: '#FAF5EF', // Warm cream background
                        borderLeft: `4px solid ${config.color}`,
                        borderBottom: `1px solid ${ABLE_COLORS.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      {isCollapsed ? (
                        <ChevronRight size={14} color={ABLE_COLORS.textMuted} />
                      ) : (
                        <ChevronDown size={14} color={ABLE_COLORS.textMuted} />
                      )}
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: config.color
                      }}>
                        {config.label}
                      </span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: ABLE_COLORS.textMuted
                      }}>
                        ({items.length})
                      </span>
                    </div>
                    
                    {/* Items in this category - hidden when collapsed */}
                    {!isCollapsed && items.map((item, idx) => 
                      renderItem(item, isLastCategory && idx === items.length - 1)
                    )}
                  </div>
                );
              })
            ) : (
              // Flat list (freshness or name sort)
              sortedItems.map((item, idx) => 
                renderItem(item, idx === sortedItems.length - 1)
              )
            )}
          </div>

          {/* Helper text */}
          <p style={{
            textAlign: 'center',
            fontSize: '13px',
            color: ABLE_COLORS.textMuted,
            marginTop: '16px'
          }}>
            Tap name to edit, cart icon to add to shopping list
          </p>
        </>
      )}
    </div>
  );
}
